import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type ConfirmedSession, type Setup } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Button, Field, TextInput } from "@/components/ui";
import {
  authError,
  Frame,
  RecoveryCodes,
  SetupAuthenticator,
} from "./AdminAuthFlow";
function readInviteToken() {
  const token =
    new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
  if (window.location.pathname === "/activate" && window.location.hash) {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
  return token;
}

// The first fragment is removed before React renders, including under StrictMode.
const initialInviteToken = readInviteToken();

export function ActivatePage() {
  const [token, setToken] = useState(initialInviteToken);
  useEffect(() => {
    function onHashChange() {
      const next = readInviteToken();
      if (next) setToken(next);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  return <InvitationFlow key={token} token={token} />;
}

function InvitationFlow({ token }: { token: string }) {
  const { t } = useTranslation();
  const { complete } = useAuth();
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof api.preview>
  > | null>(null);
  const [setup, setSetup] = useState<Setup | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedSession | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!token) return;
    let active = true;
    api
      .preview(token)
      .then((value) => {
        if (active) setPreview(value);
      })
      .catch((caught) => {
        if (active) setError(authError(caught, t));
      });
    return () => {
      active = false;
    };
  }, [token, t]);
  async function start(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError(t("adminAuth.passwordMismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setSetup(await api.inviteStart(token, password));
      setPassword("");
      setConfirm("");
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Frame>
      {confirmed ? (
        <RecoveryCodes
          codes={confirmed.recovery_codes}
          onDone={() => {
            complete(confirmed);
            setConfirmed(null);
            window.location.replace("/");
          }}
        />
      ) : setup ? (
        <SetupAuthenticator
          setup={setup}
          onConfirmed={(value) => {
            setSetup(null);
            setConfirmed(value);
          }}
          onBack={() => {
            setSetup(null);
            setError(t("adminAuth.restartInvite"));
          }}
        />
      ) : (
        <div className="space-y-4">
          <h1 className="text-lg font-semibold text-ink">
            {t("adminAuth.inviteTitle")}
          </h1>
          {preview ? (
            <>
              <p className="text-sm text-ink-subtle">
                {preview.display_name} ·{" "}
                <strong className="text-ink">{preview.email}</strong>
              </p>
              <p className="text-sm text-ink-subtle">
                {t(
                  preview.account_exists
                    ? "adminAuth.existingAccount"
                    : "adminAuth.newAccount",
                )}
              </p>
              <form onSubmit={start} className="space-y-4">
                <Field
                  label={t("adminAuth.newPassword")}
                  hint={t("adminAuth.passwordRules")}
                >
                  {(id, describedBy) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="password"
                      autoComplete="new-password"
                      minLength={12}
                      maxLength={128}
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  )}
                </Field>
                <Field label={t("adminAuth.confirmPassword")}>
                  {(id, describedBy) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirm}
                      onChange={(event) => setConfirm(event.target.value)}
                    />
                  )}
                </Field>
                {error && (
                  <p role="alert" className="text-sm text-danger">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  loading={busy}
                  disabled={
                    password.length < 12 || password.length > 128 || !confirm
                  }
                  className="w-full"
                >
                  {t("adminAuth.continue")}
                </Button>
              </form>
            </>
          ) : (
            <p role="alert" className="text-sm text-ink">
              {error ?? t(token ? "common.loading" : "adminAuth.missingInvite")}
            </p>
          )}
        </div>
      )}
    </Frame>
  );
}
